import Hourglass from "@untitled-ui/icons-react/build/esm/Hourglass03";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import Square from "@untitled-ui/icons-react/build/esm/Square";
import styled from "styled-components";
import { TooltipReorderDetails } from "./ReordersTooltips";
import { Tooltip } from "antd";
import { formatInteger } from "../../../../utils/Formatter";

// #region SCOPED CSS
const StyledReorderSuggestionTable = styled.div`
    --color-column-width: 94px;
    --size-column-width: 76px;
    --total-column-width: 94px;
    --row-height: 24px;
    --row-gap: 16px;
    --line-color: var(--backgrounds-and-lines-n-200, #262e3e);
    --cell-background: var(--backgrounds-and-lines-n-400, #1e2631);
    --matrix-background: var(--backgrounds-and-lines-n-500, #141922);
    --green: var(--text-and-icons-green-500, #00bb00);
    --orange: var(--text-and-icons-orange-500, #ff9900);
    --red: var(--text-and-icons-red-500, #ff3636);
    --blue: var(--text-and-icons-blue-500, #00aeef);
    --text: var(--text-and-icons-gray-100, #d1d1d1);

    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;

    .reorder-table {
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: center;
        justify-content: center;
        width: 720px;
    }

    .stock-grid {
        border-bottom: 1px solid var(--line-color);
        display: grid;
        grid-template-columns: var(--color-column-width) 532px var(--total-column-width);
        align-items: stretch;
        justify-content: center;
        width: 100%;
        max-height: 273px;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .color-column,
    .total-column {
        display: flex;
        flex-direction: column;
        gap: var(--row-gap);
        flex-shrink: 0;
        width: var(--color-column-width);
    }

    .total-column {
        width: var(--total-column-width);
    }

    .empty-header,
    .size-header-cell {
        border-bottom: 1px solid var(--line-color);
        display: flex;
        align-items: center;
        justify-content: center;
        height: 32px;
    }

    .color-list,
    .total-list {
        display: flex;
        flex-direction: column;
        gap: var(--row-gap);
        padding: 0 6px;
    }

    .color-name,
    .row-total,
    .grand-total {
        display: flex;
        align-items: center;
        min-height: var(--row-height);
        color: var(--text);
        font-size: 14px;
        font-weight: 400;
    }

    .color-name {
        justify-content: flex-start;
        text-align: left;
    }

    .row-total,
    .grand-total {
        justify-content: center;
        color: var(--green);
        text-align: center;
    }

    .size-matrix {
        background: var(--matrix-background);
        border-bottom: 1px solid var(--line-color);
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        overflow: hidden;
        width: 532px;
        overflow-x: auto;
    }

    .size-header {
        display: grid;
        grid-template-columns: repeat(var(--size-count), var(--size-column-width));
        min-width: min-content;
        margin: 0 auto;
    }

    .size-header-cell {
        color: var(--blue);
        font-size: 14px;
        font-weight: 400;
        text-align: center;
        line-height: normal;
    }

    .variant-rows {
        display: flex;
        flex-direction: column;
        gap: var(--row-gap);
        padding: var(--row-gap) 0 8px;
        min-width: min-content;
        margin: 0 auto;
    }

    .variant-row {
        display: grid;
        grid-template-columns: repeat(var(--size-count), var(--size-column-width));
        min-height: var(--row-height);
    }

    .variant-cell {
        display: grid;
        grid-template-columns: 16px 1fr 16px;
        gap: 2px;
        align-items: center;
        min-height: var(--row-height);
    }

    .cell-icon-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
    }

    .cell-icon {
        flex-shrink: 0;
        width: 13px;
        height: 13px;
        color: var(--text-inactive);
    }

    .cell-icon.hoverable:hover {
        color: var(--text-normal);
        cursor: pointer;
    }

    .cell-icon--urgent {
        color: var(--orange);
    }

    .quantity-cell {
        background: var(--cell-background);
        border: 1px solid transparent;
        border-radius: 4px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 22px;
        height: 24px;
        color: var(--green);
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
        text-align: center;
    }

    .quantity-cell[data-availability="available"] {
        border-color: var(--green);
    }

    .quantity-cell[data-availability="partiallyAvailable"] {
        border-color: var(--orange);
    }

    .quantity-cell[data-availability="notAvailable"] {
        border-color: var(--red);
    }

    .quantity-input {
        width: 100%;
        height: 100%;
        border: none;
        outline: none;
        background: transparent;
        color: var(--green);
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
        text-align: center;
    }

    .table-footer {
        display: grid;
        grid-template-columns: var(--color-column-width) 532px var(--total-column-width);
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .legend {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: center;
        justify-content: center;
        width: 532px;
    }

    .legend-item {
        display: flex;
        flex-direction: row;
        gap: 3px;
        align-items: center;
        justify-content: flex-start;
        color: var(--text);
        font-size: 11px;
        font-weight: 400;
        white-space: nowrap;
    }

    .legend-icon {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        position: relative;
        top: -1px;
    }

    .legend-icon--urgent,
    .legend-icon--partially-available {
        color: var(--orange);
    }

    .legend-icon--available {
        color: var(--green);
    }

    .legend-icon--not-available {
        color: var(--red);
    }
`;
// #endregion

// #region GLOBALS
export const AVAILABILITY_KEYS = {
    AVAILABLE: "available",
    PARTIALLY_AVAILABLE: "partiallyAvailable",
    NOT_AVAILABLE: "notAvailable",
    UNKNOWN: "unknown"
};

const AVAILABILITY_LEGEND_ITEMS = [
    { key: AVAILABILITY_KEYS.AVAILABLE, className: "available", label: "Supplier available" },
    {
        key: AVAILABILITY_KEYS.PARTIALLY_AVAILABLE,
        className: "partially-available",
        label: "Supplier partially available"
    },
    { key: AVAILABILITY_KEYS.NOT_AVAILABLE, className: "not-available", label: "Supplier not available" }
];
// #endregion

export default function ReorderSuggestionTable(data) {
    // #region CONSTANTS & CONFIG

    const { tableData, setTableData, hasSupplierStockAccess = false } = data || {};
    let { tableHeaders } = data;
    if (!tableHeaders) tableHeaders = { rows: [], columns: [] };
    // #endregion

    // #region METHODS
    const getRowTotal = row => {
        return row.reduce((total, variant) => total + parseQuantity(variant.quantity), 0);
    };

    const getGrandTotal = variants => {
        return variants.reduce((total, row) => total + getRowTotal(row), 0);
    };

    const parseQuantity = value => {
        const digits = String(value).replace(/\D/g, "");
        const quantity = digits ? parseInt(digits, 10) : 0;
        return Number.isFinite(quantity) ? quantity : 0;
    };

    const setVariantQuantity = (rowIndex, columnIndex, quantity) => {
        setTableData(prev => {
            return prev.map((row, currentRowIndex) => {
                if (currentRowIndex !== rowIndex) return row;

                return row.map((variant, currentColumnIndex) => {
                    if (currentColumnIndex !== columnIndex) return variant;

                    return {
                        ...variant,
                        quantity,
                        userModified: true
                    };
                });
            });
        });
    };

    const onQuantityChange = (event, rowIndex, columnIndex) => {
        const quantity = parseQuantity(event.target.value);
        setVariantQuantity(rowIndex, columnIndex, quantity);
    };
    // #endregion

    // #region COMPUTED PROPS
    const rowTotals = tableData.map(getRowTotal);
    const grandTotal = getGrandTotal(tableData);
    const formatQty = value => (value === 0 ? "0" : formatInteger(value, "0"));
    // #endregion

    // #region RENDER
    return (
        <StyledReorderSuggestionTable style={{ "--size-count": `${tableHeaders.columns.length}` }}>
            <div className="reorder-table">
                <div className="stock-grid">
                    <div className="color-column">
                        <div className="empty-header"></div>
                        <div className="color-list">
                            {tableHeaders.rows.map(color => (
                                <div className="color-name" key={color}>
                                    {color}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="size-matrix">
                        <div className="size-header">
                            {tableHeaders.columns.map(size => (
                                <div className="size-header-cell" key={size}>
                                    {size}
                                </div>
                            ))}
                        </div>
                        <div className="variant-rows">
                            {tableData.map((row, rowIndex) => (
                                <div className="variant-row" key={tableHeaders.rows[rowIndex]}>
                                    {row.map((variant, columnIndex) => (
                                        <div
                                            className="variant-cell"
                                            key={`${tableHeaders.rows[rowIndex]}-${tableHeaders.columns[columnIndex]}`}>
                                            <span className="cell-icon-slot">
                                                {variant.hasQuantityDetails && (
                                                    <Tooltip
                                                        placement="top"
                                                        title={<TooltipReorderDetails data={variant?.popupData} />}
                                                        color="var(--backgrounds-lines-n-600)"
                                                        overlayInnerStyle={{
                                                            width: "300px"
                                                        }}
                                                        overlayClassName="custom-tool-tip">
                                                        <InfoCircle className="cell-icon hoverable" />
                                                    </Tooltip>
                                                )}
                                            </span>
                                            <span
                                                className="quantity-cell"
                                                data-availability={variant.availability || undefined}>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    className="quantity-input"
                                                    value={formatQty(parseQuantity(variant.quantity))}
                                                    onChange={e => onQuantityChange(e, rowIndex, columnIndex)}
                                                />
                                            </span>
                                            <span className="cell-icon-slot">
                                                {variant.urgent && (
                                                    <Hourglass className="cell-icon cell-icon--urgent" />
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="total-column">
                        <div className="empty-header"></div>
                        <div className="total-list">
                            {rowTotals.map((total, index) => (
                                <div className="row-total" key={tableHeaders.rows[index]}>
                                    {formatQty(total)}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="table-footer">
                    <div></div>
                    <div className="legend">
                        <div className="legend-item">
                            <Hourglass className="legend-icon legend-icon--urgent" />
                            <span>Urgent</span>
                        </div>
                        {hasSupplierStockAccess ? (
                            AVAILABILITY_LEGEND_ITEMS.map(item => (
                                <div className="legend-item" key={item.key}>
                                    <Square className={`legend-icon legend-icon--${item.className}`} />
                                    <span>{item.label}</span>
                                </div>
                            ))
                        ) : (
                            <div className="legend-item text-orange">
                                <span>
                                    This supplier doesn't provide stock availability. Order will be sent if accepted by
                                    you.
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="grand-total">{formatQty(grandTotal)}</div>
                </div>
            </div>
        </StyledReorderSuggestionTable>
    );
    // #endregion
}
